<template>
  <div class="vp-job-monitoring">
    <!-- Status Block -->
    <div
      v-if="!isLoadingStatusBlock"
      id="job-status-block"
      class="wrap-job-status-block"
    >
      <div
        v-for="(item, index) in jobStatusBlock"
        :key="index"
        class="d-flex">
        <div
          :style="styleDivBlock(item)"
          :class="['container-job-status',
                   { 'container-job-status-active': indexBlockActive === index,
                     'mr-0' : index === 2 }]"
          @click="changeSummaryBlock(item, index)">
          <div class="item-name ellipsis">{{ item.name }}</div>
          <div
            v-if="load_first"
            class="item-count">{{ convertCountNumber(item.count) }}</div>
          <div
            v-else
            class="item-count">-</div>
        </div>

        <div
          v-if="index === 2"
          class="line"/>
      </div>
    </div>

    <!-- Loading Status Block -->
    <div
      v-if="isLoadingStatusBlock"
      class="wrap-job-status-block-loading"
    >
      <div
        v-for="i in 6"
        :key="`block-${i}`"
        class="container-job-status"
      />
    </div>

    <v-server-table
      v-show="load_first"
      ref="jobMonitoringTable"
      :columns="columns"
      :options="options"
      class="jTable"
      @loading="loading"
      @loaded="loaded"
    >
      <div
        slot="beforeFilter"
        class="custom-search">
        <b-form inline>
          <b-button
            class="btn-refresh mr-2"
            @click="refreshJobMonitoringTable">
            <i class="fa fa-refresh"/>
            <span>Refresh</span>
          </b-button>
          <b-button
            v-if="selectedStatus === 'waiting'"
            class="btn-cancel-waiting"
            @click="toggleModalCancelJob(true)"
          >
            <i class="fa fa-ban"/>
            <span>Cancel All Waiting</span>
          </b-button>
          <b-button
            v-if="selectedStatus === 'cancel' || selectedStatus === 'error'"
            class="btn-retry"
            @click="toggleModalRetryJob(true, 'all')"
          >
            <img src="@/assets/icon/retry.svg">
            <span>Retry All</span>
          </b-button>
        </b-form>
      </div>

      <div
        slot="start_time"
        slot-scope="props">
        {{ convertDateTime(props.row.start_time) }}
      </div>
      <div
        slot="end_time"
        slot-scope="props">
        {{ convertDateTime(props.row.end_time) }}
      </div>
      <div
        slot="status"
        slot-scope="props"
        class="text-capitalize">
        <img
          :src="require(`@/assets/log/${props.row.status}.png`)"
          class="m__r--5">
        {{ props.row.status === 'success' ? 'Successfully' : `${props.row.status}` }}
      </div>
      <div
        v-if="selectedStatus === 'error' || selectedStatus === 'cancel'"
        slot="status_message"
        slot-scope="props"
        class="text-uppercase">
        {{ props.row.status_message }}
      </div>
      <div
        slot="runtime"
        slot-scope="props">
        <j-timer
          v-if="selectedStatus === 'running'"
          :time="props.row.start_time"
        />
        <template v-else>
          {{ props.row.runtime || 0 }} ms
        </template>
      </div>
      <div
        v-if="selectedStatus === 'error' || selectedStatus === 'cancel'"
        slot="action"
        slot-scope="props">
        <b-button
          class="btn-retry btn-retry--table"
          @click="toggleModalRetryJob(true, 'each', props.row.running_id)"
        >
          <img src="@/assets/icon/retry.svg">
          <span>Retry</span>
        </b-button>
      </div>
    </v-server-table>

    <div v-if="!load_first">
      <jLoadingTable/>
    </div>

    <ModalCancelJob
      v-if="isOpenModalCancelJob"
      ref="modalCancelJob"
      :visprog-id="id"
      @close="toggleModalCancelJob"
      @completed="refreshJobMonitoringTable"
    />

    <ModalRetryJob
      v-if="isOpenModalRetryJob"
      ref="modalRetryJob"
      :visprog-id="id"
      :is-retry-all="isRetryAllJob"
      :run-history-id="selectedRunHistory"
      :status="selectedStatus"
      @close="toggleModalRetryJob"
      @completed="refreshJobMonitoringTable"
    />

    <LoadingScreenJobMonitoring/>
  </div>
</template>

<script>
import jApi from '@/services/jApi';
import { mapActions } from 'vuex';
import jLoadingTable from '@/components/skeleton-loading/jLoadingTable';
import ModalCancelJob from '@/views/visual-programming/popup/ModalCancelJob';
import ModalRetryJob from '@/views/visual-programming/popup/ModalRetryJob';
import LoadingScreenJobMonitoring from '@/views/visual-programming/popup/LoadingScreenJobMonitoring';
import jTimer from '@/components/timer/jTimer';

export default {
  components: {
    jLoadingTable,
    ModalCancelJob,
    ModalRetryJob,
    LoadingScreenJobMonitoring,
    jTimer,
  },

  props: {
    id: { type: String, default: '', required: true },
  },

  data() {
    return {
      isLoadingStatusBlock: false,
      isOpenModalCancelJob: false,
      isOpenModalRetryJob: false,
      isShowLoader: false,
      isRetryAllJob: false,
      selectedRunHistory: null,
      intervalUpdateJobFunction: null,
      indexBlockActive: 0,
      load_first: false,
      selectedStatus: 'running',
      jobStatusBlock: [
        {
          name: 'running',
          count: 0,
          color: '#F36F21',
        },
        {
          name: 'waiting',
          count: 0,
          color: '#73189D',
        },
        {
          name: 'pending',
          count: 0,
          color: '#606060',
        },
        {
          name: 'success',
          count: 0,
          color: '#0C9C68',
        },
        {
          name: 'cancel',
          count: 0,
          color: '#4D3923',
        },
        {
          name: 'error',
          count: 0,
          color: '#D0021B',
        },
      ],

      columns: ['running_id', 'start_time', 'end_time', 'status', 'runtime'],
      options: {
        headings: {
          running_id: 'RUNNING ID',
          start_time: 'START TIME',
          end_time: 'END TIME',
          status: 'STATUS',
          status_message: 'MESSAGE',
          runtime: 'RUNTIME',
          action: 'ACTION',
        },
        texts: {
          limit: 'Rows per page',
        },
        sortIcon: {
          base: 'fa',
          up: 'fa-chevron-up',
          down: 'fa-chevron-down',
          is: 'fa-sort',
        },
        filterable: true,
        debounce: 1000,
        sortable: ['start_time'],
        perPage: 10,
        perPageValues: [10, 20, 50, 100],

        requestAdapter(data) {
          return {
            pagination: {
              page: data.page,
              limit: data.limit,
              filter: {
                status: this.selectedStatus,
              },
              sort: { start_time: data.ascending ? 1 : -1 },
            },
          };
        },
        requestFunction(data) {
          const api = jApi.generateApi();
          const getList = api.get(`nocode-job-monitor/jobs/${data.visprogId}`, { params: data.pagination }).then(res => ({
            data: res.data,
          }));
          return getList.catch((e) => {
            this.$store.dispatch('jStoreNotificationScreen/getProblemMessage', e.response.data.errors[0].message).bind(this);
          });
        },
        responseAdapter({ data }) {
          return {
            data: data.data || [],
            count: data.length || 0,
          };
        },
      },
    };
  },

  watch: {
    selectedStatus(val) {
      if (val === 'error') {
        this.columns = ['running_id', 'start_time', 'end_time', 'status', 'status_message', 'runtime', 'action'];
      } else if (val === 'cancel') {
        this.columns = ['running_id', 'start_time', 'end_time', 'status', 'status_message', 'action'];
      } else if (val === 'success' || val === 'running') {
        this.columns = ['running_id', 'start_time', 'end_time', 'status', 'runtime'];
      } else {
        this.columns = ['running_id', 'start_time', 'end_time', 'status'];
      }
    },
    isShowLoader(val) {
      if (val) {
        this.load_first = false;
      } else {
        this.load_first = true;
      }
    },
  },

  created() {
    this.getCountJobStatus();
  },

  mounted() {
    this.handleInterval();
  },

  beforeUnmount() {
    clearInterval(this.intervalUpdateJobFunction);
  },

  methods: {
    ...mapActions('jStoreNotificationScreen', ['showLoading', 'hideLoading']),

    styleDivBlock(item) {
      return { '--color': item.color };
    },

    changeSummaryBlock(item, index) {
      this.indexBlockActive = index;
      this.selectedStatus = item.name;
      this.getCountJobStatus();
      this.$refs.jobMonitoringTable.setPage(1);
    },

    refreshJobMonitoringTable() {
      this.getCountJobStatus();
      this.$refs.jobMonitoringTable.refresh();
    },

    convertDateTime(data) {
      if (data) {
        const date = new Date(data);
        const years = date.getFullYear();
        const mounth = date.getMonth() + 1 < 10 ? `0${date.getMonth() + 1}` : date.getMonth() + 1;
        const dates = date.getDate() < 10 ? `0${date.getDate()}` : date.getDate();

        const hours = date.getHours() < 10 ? `0${date.getHours()}` : date.getHours();
        const minutes = date.getMinutes() < 10 ? `0${date.getMinutes()}` : date.getMinutes();
        const secound = date.getSeconds() < 10 ? `0${date.getSeconds()}` : date.getSeconds();

        return `${years}-${mounth}-${dates} ${hours}:${minutes}:${secound}`;
      }
      return '-';
    },

    loading(val) {
      const a = val;
      a.pagination.filter = {
        status: this.selectedStatus,
      };
      a.visprogId = this.id;
      this.toggleLoader(true);
      // this.load_first = false;
    },

    loaded(val) {
      if (val && val.data) {
        this.jobStatusBlock[this.indexBlockActive].count = val.data.length;
      }
      this.toggleLoader(false);
      // this.load_first = true;
    },

    async getMonitoringList(params) {
      const api = jApi.generateApi();
      const res = await api.get(`nocode-job-monitor/jobs/${this.id}`, { params });
      return res;
    },

    getCountJobStatus() {
      this.isLoadingStatusBlock = true;
      this.jobStatusBlock.forEach((item, index) => {
        const params = {
          page: 1,
          limit: 1,
          filter: {
            status: item.name,
          },
        };
        this.getMonitoringList(params)
          .then((res) => {
            if (!res.data.error) {
              this.jobStatusBlock[index].count = res.data.length;
            }
          });
      });
      this.isLoadingStatusBlock = false;
    },

    toggleModalCancelJob(status) {
      if (status) {
        this.isOpenModalCancelJob = true;
        setTimeout(() => {
          const element = document.getElementById('idModalCancelJob');
          if (element) element.classList.add('show');
        }, 200);
      } else {
        const element = document.getElementById('idModalCancelJob');
        if (element) element.classList.remove('show');

        setTimeout(() => {
          this.isOpenModalCancelJob = false;
        }, 200);
      }
    },

    toggleModalRetryJob(status, mode, runningId) {
      if (status) {
        this.isRetryAllJob = !!(mode && mode === 'all');
        this.isOpenModalRetryJob = true;
        this.selectedRunHistory = runningId;
        setTimeout(() => {
          const element = document.getElementById('idModalRetryJob');
          if (element) element.classList.add('show');
        }, 200);
      } else {
        const element = document.getElementById('idModalRetryJob');
        if (element) element.classList.remove('show');

        setTimeout(() => {
          this.isOpenModalRetryJob = false;
          this.isRetryAllJob = false;
        }, 200);
      }
    },

    toggleLoader(status) {
      if (status) {
        this.isShowLoader = true;
        const element = document.getElementById('idLoaderJobMonitoring');
        if (element) element.classList.add('show');
      } else {
        const element = document.getElementById('idLoaderJobMonitoring');

        setTimeout(() => {
          if (element) element.classList.remove('show');
          this.isShowLoader = false;
        }, 350);
      }
    },

    handleInterval() {
      this.intervalUpdateJobFunction = setInterval(() => {
        if (this.$refs.jobMonitoringTable) {
          this.refreshJobMonitoringTable();
        }
      }, 30000);
    },

    convertCountNumber(count) {
      if (count >= 1e12) return `${+(count / 1e12).toFixed(1)}Trillion`;
      else if (count >= 1e9 && count < 1e12) return `${+(count / 1e9).toFixed(1)}Billion`;
      else if (count >= 1e7 && count < 1e9) return `${+(count / 1e6).toFixed(1)}Million`;
      return count;
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/views/visual-programming/jVPJobMonitoring.vue